<template>
  <base-dialog
    :action="onAction"
    :action-name="'Add service'"
    :close="onClose"
    :title="$t('services.add_service')"
  >
    <template #activator="{ on }">
      <slot
        name="activator"
        v-bind="{ on }"
      />
    </template>

    <template #content>
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="filter"
            label="Search"
            class="mb-3"
            prepend-inner-icon="search"
            block
            outlined
            hide-details
            clearable
          />

          <v-alert
            dense
            outlined
            text
            color="info"
            icon="info"
          >
            Only showing service products with matching currency:
            ({{ currency }} {{ interval }} {{ intervalCount }}).
          </v-alert>

          <v-layout
            v-if="isLoading"
            align-center
            justify-center
            row
            fill-height
          >
            <v-progress-circular
              :size="125"
              :width="10"
              color="primary"
              indeterminate
            />
          </v-layout>

          <v-layout v-if="!isLoading">
            <v-flex xs12>
              <v-list>
                <template
                  v-for="(services, index) in items"
                >
                  <v-list-group
                    :key="`service_${index}`"
                  >
                    <template v-slot:activator>
                      <v-list-item-title>{{ services.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ services.plans.length }} pricing plans</v-list-item-subtitle>
                    </template>

                    <v-list-item-group
                      v-model="selectedPlan"
                    >
                      <template
                        v-for="(plan, planIndex) in services.plans"
                      >
                        <v-list-item
                          :key="`plan_${planIndex}`"
                          :disabled="isIdAdded(plan.id) || plan.active === false"
                          :value="plan"
                        >
                          <v-list-item-avatar />
                          <v-list-item-content>
                            <v-list-item-title>{{ plan.nickname }}</v-list-item-title>
                            <v-list-item-subtitle>{{ terms(plan) }}</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-chip
                              v-if="isIdAdded(plan.id)"
                              small
                              color="info"
                            >
                              In basket
                            </v-chip>

                            <v-chip
                              v-if="plan.active === false"
                              small
                              color="grey"
                            >
                              Archived
                            </v-chip>
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                    </v-list-item-group>
                  </v-list-group>
                </template>
              </v-list>
            </v-flex>
          </v-layout>
        </v-col>
      </v-row>
    </template>
  </base-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseDialog from "../Common/BaseDialog"
import { readablePlanTerms } from '../../services/stripe.service'

export default {
  components: { BaseDialog },
  props: {
    currency: {
      type: String,
      default: ''
    },
    interval: {
      type: String,
      default: null
    },
    intervalCount: {
      type: Number,
      default: null
    },
    addedIds: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters([ 'getPlans' ]),
    items() {
      /**
       * Since we can expand the product in the plan, we want to then
       * organize the products first with plans under them
       */
      return Object.values(
        this.getPlans.reduce((prev, plan) => {
          const { product, id: planId, interval, interval_count, currency } = plan
          const { id: serviceId } = product

          const hasIntervalAndCount = this.interval && this.intervalCount

          const addPlanToService = () => {
            // All conditions are subject to currency
            if (this.currency === currency) {
              if (serviceId in prev) {
                prev[ serviceId ].plans.push(plan)
              } else {
                prev[ serviceId ] = product
                prev[ serviceId ].plans = [ plan ]
              }
            }
          }

          if (hasIntervalAndCount) {
            // Only add plans that match the interval & count, if defined
            if (this.interval === interval
              && this.intervalCount === interval_count) {
              addPlanToService()
            }
          } else {
            addPlanToService()
          }

          return prev
        }, {})
      )
    }
  },
  data() {
    return {
      filter: '',
      isLoading: false,
      selectedPlan: null,
    }
  },
  async mounted() {
    const payload = {
      query: {
        expand: [ 'data.product' ]
      }
    }

    this.isLoading = true
    await this.listPlans(payload)
      .finally(() => {
        this.isLoading = false
      })
  },
  methods: {
    ...mapActions([ 'listPlans' ]),
    onClose() {
      this.$emit('close')
    },
    isIdAdded(id){
      return this.addedIds.includes(id)
    },
    terms(plan) {
      return readablePlanTerms(plan)
    },
    async onAction() {
      this.$emit('action', this.selectedPlan)

      this.selectedPlan = null
    }
  }
}
</script>
